/**
 * AI Interview Practice System - Main App Component
 *
 * Copyright (c) 2024 Technical Interviewer Attempt. All Rights Reserved.
 * Proprietary and Confidential. Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Auth/Login';
import InterviewSetup from './components/InterviewSetup';
import InterviewSession from './components/InterviewSession';
import SessionHistory from './components/SessionHistory';
import RecordingsList from './components/RecordingsList';
import GuestDemo from './components/GuestDemo';
import PricingPage from './components/Pricing/PricingPage';
import LegalPage, { type LegalDocument } from './components/Legal/LegalPage';
import BillingSuccess from './components/Billing/BillingSuccess';
import AccountBilling from './components/Billing/AccountBilling';
import { getUserPlan } from './utils/plans';

const LEGAL_PATHS: Record<string, LegalDocument> = {
  '/terms': 'terms',
  '/privacy': 'privacy',
  '/refunds': 'refunds',
};

const currentPath = (): string =>
  window.location.pathname.replace(/\/+$/, '') || '/';

// App state keys for localStorage
const STATE_KEY = 'ai_interview_app_state';

interface InterviewConfig {
  type?: string;
  difficulty?: string;
  isMultiSession?: boolean;
  interviews?: Array<{
    id: number;
    type: string;
    name: string;
    icon: string;
    duration: number;
    difficulty: string;
    order?: number;
  }>;
  totalDuration?: number;
  sessionId?: string;
}

interface AppState {
  sessionStarted: boolean;
  interviewConfig: InterviewConfig | null;
  currentView: string;
  timestamp: number;
}

const AppContent: React.FC = () => {
  const { user, loading, signOut, refreshAccount } = useAuth();
  const [sessionStarted, setSessionStarted] = useState<boolean>(false);
  const [interviewConfig, setInterviewConfig] =
    useState<InterviewConfig | null>(null);
  const [currentView, setCurrentView] = useState<
    'setup' | 'session' | 'history' | 'recordings'
  >('setup');
  const isAccountUser = user?.accountType === 'account';

  // Plans change outside this tab (Stripe webhooks, another device), so re-read on load.
  useEffect(() => {
    if (isAccountUser) refreshAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccountUser]);

  // Load persisted state on mount
  useEffect(() => {
    if (user) {
      try {
        const savedState = localStorage.getItem(
          `${STATE_KEY}_${user.username || user.email}`
        );
        if (savedState) {
          const parsed: AppState = JSON.parse(savedState);
          // Only restore if session was in progress
          if (parsed.sessionStarted && parsed.interviewConfig) {
            setSessionStarted(true);
            setInterviewConfig(parsed.interviewConfig);
            setCurrentView('session');
          }
        }
      } catch (error) {
        console.error('Error loading persisted state:', error);
      }
    }
  }, [user]);

  // Save state when it changes
  useEffect(() => {
    if (user && sessionStarted && interviewConfig) {
      try {
        const stateToSave: AppState = {
          sessionStarted,
          interviewConfig,
          currentView,
          timestamp: Date.now(),
        };
        localStorage.setItem(
          `${STATE_KEY}_${user.username || user.email}`,
          JSON.stringify(stateToSave)
        );
      } catch (error) {
        console.error('Error saving state:', error);
      }
    }
  }, [user, sessionStarted, interviewConfig, currentView]);

  const handleStartInterview = useCallback((config: InterviewConfig) => {
    setInterviewConfig(config);
    setSessionStarted(true);
    setCurrentView('session');
  }, []);

  const handleEndInterview = useCallback(() => {
    setSessionStarted(false);
    setInterviewConfig(null);
    setCurrentView('setup');
    // Clear persisted state
    if (user) {
      localStorage.removeItem(`${STATE_KEY}_${user.username || user.email}`);
    }
  }, [user]);

  const handleViewHistory = useCallback(() => {
    setCurrentView('history');
  }, []);

  const handleBackFromHistory = useCallback(() => {
    setCurrentView('setup');
  }, []);

  const handleViewRecordings = useCallback(() => {
    setCurrentView('recordings');
  }, []);

  const handleBackFromRecordings = useCallback(() => {
    setCurrentView('setup');
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
        <div className="text-center text-white">
          <div className="w-12 h-12 border-4 border-indigo-300 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-700">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return <Login />;
  }

  if (getUserPlan(user) === 'none') {
    return <PricingPage notice="Choose a plan to start practising" />;
  }

  // Show session history
  if (currentView === 'history') {
    return (
      <div className="min-h-screen flex flex-col">
        <SessionHistory user={user} onBack={handleBackFromHistory} />
      </div>
    );
  }

  // Show recordings list
  if (currentView === 'recordings') {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
        <div className="bg-white border-b-2 border-slate-200 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h1 className="text-slate-800 text-xl sm:text-2xl font-semibold m-0">
              Interview Recordings
            </h1>
            <button
              onClick={handleBackFromRecordings}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 rounded-lg transition-all duration-200 text-sm"
            >
              ← Back to Setup
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          <RecordingsList username={user.username || user.email || ''} />
        </div>
      </div>
    );
  }

  // Show main app content
  return (
    <div className="min-h-screen flex flex-col">
      {/* User header with logout - only show when not in interview */}
      {!sessionStarted && currentView === 'setup' && (
        <div className="fixed top-0 right-0 p-3 px-5 flex items-center gap-3 z-[1000] bg-white/95 backdrop-blur-md rounded-bl-xl shadow-lg">
          {user.accountType === 'account' && (
            <a
              href="/account"
              className="text-sm font-medium text-indigo-600 no-underline hover:text-indigo-800"
            >
              {getUserPlan(user) === 'pro' ? 'Pro' : 'Standard'} · Plan &amp; billing
            </a>
          )}
          <button
            className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
            onClick={signOut}
          >
            Sign Out
          </button>
        </div>
      )}

      {currentView === 'setup' && !sessionStarted ? (
        <InterviewSetup
          onStart={handleStartInterview}
          onViewHistory={handleViewHistory}
          onViewRecordings={handleViewRecordings}
        />
      ) : interviewConfig ? (
        <InterviewSession
          config={interviewConfig}
          onEnd={handleEndInterview}
          user={user}
          signOut={signOut}
        />
      ) : null}
    </div>
  );
};

const AccountRoute: React.FC = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <AccountBilling /> : <Login />;
};

const App: React.FC = () => {
  const path = currentPath();

  // Public route: rendered outside the auth guard.
  if (path === '/demo') {
    return <GuestDemo />;
  }

  let page: React.ReactNode;
  if (LEGAL_PATHS[path]) {
    page = <LegalPage document={LEGAL_PATHS[path]} />;
  } else if (path === '/pricing') {
    page = <PricingPage />;
  } else if (path === '/billing/success') {
    page = <BillingSuccess />;
  } else if (path === '/account') {
    page = <AccountRoute />;
  } else {
    page = <AppContent />;
  }

  return <AuthProvider>{page}</AuthProvider>;
};

export default App;
